import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ProGate from "@/components/ProGate";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Plus, Camera, Upload, Download, FileText, Trash2, Loader2,
  Info, TrendingUp, TrendingDown, Calculator, AlertTriangle,
  Car, Landmark, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const DISCLAIMER =
  "Estimates only — not tax advice. AI helps but isn't perfect. Confirm every number with your accountant.";

const centsToDollars = (c) => `$${(Math.round((c || 0)) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function VendorBookkeeping() {
  return (
    <ProGate
      feature="Bookkeeping"
      pitch="Track income and expenses, snap a receipt to log it with AI, auto-import your booth fees from Stripe, log mileage at the current IRS rate, track sales tax you're holding for the state, and export a Schedule C summary at tax time."
      perks={[
        "AI receipt scanner powered by Claude Vision",
        "Auto-import every booth fee you pay through AddVendTour",
        "IRS-rate-aware mileage log with a live deduction preview",
        "Sales tax tracker: know exactly what you owe each state",
        "Schedule C PDF + CSV export at tax time",
      ]}
    >
      <VendorBookkeepingInner />
    </ProGate>
  );
}

function VendorBookkeepingInner() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [salesTax, setSalesTax] = useState(null);
  const [myEvents, setMyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [mileageOpen, setMileageOpen] = useState(false);
  const [salesTaxOpen, setSalesTaxOpen] = useState(false);

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= currentYear - 3; y -= 1) arr.push(y);
    return arr;
  }, [currentYear]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [cats, sum, tx, st, ev] = await Promise.all([
        api.get("/bookkeeping/categories"),
        api.get(`/bookkeeping/summary?year=${year}`),
        api.get(`/bookkeeping/transactions?year=${year}`),
        api.get(`/bookkeeping/sales-tax?year=${year}`),
        api.get("/vendor/my-events"),
      ]);
      setCategories(cats.data);
      setSummary(sum.data);
      setItems(tx.data.items);
      setSalesTax(st.data);
      setMyEvents(ev.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [year]);

  const importStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookkeeping/import-stripe");
      if (data.added === 0 && data.skipped === 0) {
        toast.info("No paid booth fees found yet.");
      } else {
        toast.success(`Imported ${data.added} booth fee(s) · ${data.skipped} already logged.`);
      }
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteTx = async (id) => {
    if (!window.confirm("Delete this transaction? This can't be undone.")) return;
    try {
      await api.delete(`/bookkeeping/transactions/${id}`);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadUrl = (path) => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return `${backend}/api${path}`;
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="vendor-bookkeeping-page">
      <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
        <Sparkles className="h-3 w-3" /> Books & taxes
      </div>
      <div className="mt-3 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-serif text-5xl">Bookkeeping.</h1>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
            Track income and expenses, auto-import booth fees, snap a receipt to log it, and export a Schedule C summary at tax time.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Tax year</label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="bookkeeping-year-select" className="w-28 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Fine-print disclaimer */}
      <div
        data-testid="bookkeeping-disclaimer"
        className="mt-6 flex items-start gap-2.5 rounded-md p-3 text-xs"
        style={{ backgroundColor: "rgba(212,88,58,0.06)", border: "1px solid rgba(212,88,58,0.20)",
                  color: "var(--ink-soft)" }}
      >
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--terracotta)" }} />
        <div>
          <strong style={{ color: "var(--terracotta)" }}>Fine print.</strong> {DISCLAIMER}
        </div>
      </div>

      {/* KPI cards */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Income YTD"
          value={summary ? centsToDollars(summary.income_cents) : "—"}
          icon={TrendingUp}
          tone="forest"
          testId="kpi-income"
        />
        <KpiCard
          label="Expenses YTD"
          value={summary ? centsToDollars(summary.expense_cents) : "—"}
          icon={TrendingDown}
          tone="terracotta"
          testId="kpi-expense"
        />
        <KpiCard
          label="Net profit"
          value={summary ? centsToDollars(summary.net_income_cents) : "—"}
          icon={Calculator}
          tone="gold"
          testId="kpi-net"
        />
        <KpiCard
          label="Est. tax due (rough)"
          value={summary ? centsToDollars(summary.tax_estimate.total_estimated_tax_cents) : "—"}
          icon={FileText}
          tone="ink"
          testId="kpi-tax"
          subtitle="Not tax advice"
        />
      </div>

      {/* Quarterly card */}
      {summary && (
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <div
            data-testid="bookkeeping-quarterly-card"
            className="lg:col-span-2 card-warm rounded-lg p-5"
            style={{ backgroundColor: "#fff", borderLeft: "3px solid var(--forest)" }}
          >
            <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--forest)" }}>
              <Calculator className="h-3 w-3" /> Estimated quarterly tax
            </div>
            <div className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>
              A very rough guide of what to set aside every quarter — assumes ~15.3% self-employment tax + ~12% federal income tax on your net profit so far.
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-4">
              {["Q1", "Q2", "Q3", "Q4"].map((q, i) => (
                <div key={q} data-testid={`quarterly-${q.toLowerCase()}`}
                      className="rounded-md p-4 text-center"
                      style={{ border: "1px solid rgba(0,0,0,0.06)", backgroundColor: "#fbf8f2" }}>
                  <div className="text-xs uppercase tracking-wider" style={{ color: "var(--ink-soft)" }}>{q}</div>
                  <div className="font-serif text-2xl mt-1" style={{ color: "var(--forest)" }}>
                    {centsToDollars(summary.tax_estimate.quarterly_cents[i])}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mileage summary */}
          <div
            data-testid="mileage-summary-card"
            className="card-warm rounded-lg p-5"
            style={{ backgroundColor: "#fff", borderLeft: "3px solid var(--gold)" }}
          >
            <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--gold)" }}>
              <Car className="h-3 w-3" /> Mileage
            </div>
            <div className="mt-3">
              <div className="font-serif text-3xl" style={{ color: "var(--gold)" }}>
                {summary.mileage?.total_miles ?? 0} <span className="text-base" style={{ color: "var(--ink-soft)" }}>mi</span>
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>logged in {year}</div>
            </div>
            <div className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
              Current IRS rate: <strong>{summary.mileage?.current_rate_cents_per_mile ?? 0}¢/mi</strong>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMileageOpen(true)}
              data-testid="mileage-add-btn"
              className="mt-4 w-full rounded-full"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Log a trip
            </Button>
          </div>
        </div>
      )}

      {/* Action row */}
      <div className="mt-6 flex flex-wrap gap-2">
        <Button onClick={() => setAddOpen(true)} data-testid="add-tx-btn"
                className="btn-primary rounded-full">
          <Plus className="h-4 w-4 mr-1.5" /> Add transaction
        </Button>
        <Button onClick={() => setScanOpen(true)} data-testid="scan-receipt-btn"
                variant="outline" className="rounded-full">
          <Camera className="h-4 w-4 mr-1.5" /> Scan a receipt
        </Button>
        <Button onClick={importStripe} disabled={busy} data-testid="import-stripe-btn"
                variant="outline" className="rounded-full">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
          Import booth fees
        </Button>
        <Button onClick={() => setMileageOpen(true)} data-testid="log-mileage-btn"
                variant="outline" className="rounded-full">
          <Car className="h-4 w-4 mr-1.5" /> Log mileage
        </Button>
        <Button onClick={() => setCsvOpen(true)} data-testid="import-csv-btn"
                variant="outline" className="rounded-full">
          <Upload className="h-4 w-4 mr-1.5" /> Import CSV
        </Button>
        <a href={downloadUrl(`/bookkeeping/export/tax-pack?year=${year}`)}
           data-testid="export-tax-pack-btn"
           className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors"
           style={{
             backgroundColor: "var(--terracotta)",
             color: "#fff",
             boxShadow: "0 2px 8px rgba(201,67,42,0.25)",
           }}
           title="One-click bundle: README + Schedule C PDF + full transaction CSV. Hand to your CPA or paste into TurboTax Self-Employed.">
          <Sparkles className="h-4 w-4" /> Download Tax Pack ({year})
        </a>
        <a href={downloadUrl(`/bookkeeping/export/schedule-c?year=${year}`)}
           data-testid="export-schedule-c-btn"
           className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border transition-colors hover:bg-[rgba(31,93,52,0.06)]"
           style={{ borderColor: "rgba(0,0,0,0.10)", color: "var(--forest)" }}>
          <FileText className="h-4 w-4" /> Schedule C PDF
        </a>
        <a href={downloadUrl(`/bookkeeping/export/csv?year=${year}`)}
           data-testid="export-csv-btn"
           className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border transition-colors hover:bg-[rgba(0,0,0,0.03)]"
           style={{ borderColor: "rgba(0,0,0,0.10)", color: "var(--ink)" }}>
          <Download className="h-4 w-4" /> CSV
        </a>
      </div>

      {/* Transactions table */}
      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-3">Transactions <span className="text-sm font-sans" style={{ color: "var(--ink-soft)" }}>· {items.length}</span></h2>
        {loading ? (
          <div className="text-center py-12" style={{ color: "var(--ink-soft)" }}>
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div data-testid="bookkeeping-empty"
                className="card-warm rounded-lg p-10 text-center"
                style={{ backgroundColor: "#fff" }}>
            <div className="font-serif text-xl">Nothing logged yet for {year}.</div>
            <div className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              Add a transaction, snap a receipt, or click "Import booth fees" to pull every fee you've paid through AddVendTour.
            </div>
          </div>
        ) : (
          <div className="card-warm rounded-lg overflow-hidden" style={{ backgroundColor: "#fff" }}>
            <table className="w-full text-sm" data-testid="bookkeeping-tx-table">
              <thead>
                <tr style={{ backgroundColor: "#fbf8f2", color: "var(--ink-soft)" }}>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Description</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                  <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} data-testid={`tx-row-${t.id}`}
                      className="border-t" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
                    <td className="px-4 py-3 whitespace-nowrap">{t.date}</td>
                    <td className="px-4 py-3">
                      {t.description || <span style={{ color: "var(--ink-soft)" }}>—</span>}
                      {t.event_title && (
                        <span
                          data-testid={`tx-event-${t.id}`}
                          className="ml-2 text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                          style={{
                            backgroundColor: "rgba(31,93,52,0.08)",
                            color: "var(--forest)",
                          }}
                        >
                          {t.event_title}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: t.type === "income" ? "rgba(31,93,52,0.10)" : "rgba(212,88,58,0.10)",
                              color: t.type === "income" ? "var(--forest)" : "var(--terracotta)",
                            }}>
                        {t.category_label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono" style={{ color: t.type === "income" ? "var(--forest)" : "var(--terracotta)" }}>
                      {t.type === "income" ? "+" : "−"}{centsToDollars(t.amount_cents)}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--ink-soft)" }}>
                      {t.source}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => deleteTx(t.id)}
                        data-testid={`tx-delete-${t.id}`}
                        className="text-xs opacity-40 hover:opacity-100 transition-opacity"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sales tax section */}
      <SalesTaxSection
        year={year}
        salesTax={salesTax}
        onAdd={() => setSalesTaxOpen(true)}
        onChanged={refresh}
      />

      <AddTransactionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        categories={categories}
        onSaved={refresh}
      />
      <ScanReceiptDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        categories={categories}
        onSaved={refresh}
      />
      <ImportCsvDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        onSaved={refresh}
      />
      <MileageDialog
        open={mileageOpen}
        onOpenChange={setMileageOpen}
        myEvents={myEvents}
        onSaved={refresh}
      />
      <SalesTaxDialog
        open={salesTaxOpen}
        onOpenChange={setSalesTaxOpen}
        myEvents={myEvents}
        onSaved={refresh}
      />
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone, testId, subtitle }) {
  const color = {
    forest: "var(--forest)", terracotta: "var(--terracotta)",
    gold: "var(--gold)", ink: "var(--ink)",
  }[tone] || "var(--ink)";
  return (
    <div data-testid={testId} className="card-warm rounded-lg p-5" style={{ backgroundColor: "#fff" }}>
      <div className="overline inline-flex items-center gap-1.5" style={{ color }}>
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-serif text-3xl mt-2" style={{ color }}>{value}</div>
      {subtitle && <div className="text-xs italic mt-1" style={{ color: "var(--ink-soft)" }}>{subtitle}</div>}
    </div>
  );
}

function AddTransactionDialog({ open, onOpenChange, categories, onSaved }) {
  const [type, setType] = useState("expense");
  const [category, setCategory] = useState("supplies");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Reset category when type flips
    setCategory(type === "income" ? "sales" : "supplies");
  }, [type]);

  useEffect(() => {
    if (!open) {
      setType("expense"); setDate(new Date().toISOString().slice(0, 10));
      setAmount(""); setDescription("");
    }
  }, [open]);

  const submit = async () => {
    const dollars = parseFloat(amount);
    if (!dollars || dollars <= 0) { toast.error("Enter a positive amount."); return; }
    setSaving(true);
    try {
      await api.post("/bookkeeping/transactions", {
        date, type, category,
        amount_cents: Math.round(dollars * 100),
        description,
      });
      toast.success("Transaction logged.");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const options = type === "income" ? categories.income : categories.expense;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-tx-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Add transaction</DialogTitle>
          <DialogDescription>Log income earned or an expense you paid. Small notes help at tax time.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setType("expense")}
              data-testid="add-tx-type-expense"
              className={`px-3 py-2 rounded-md text-sm border transition-colors ${type === "expense" ? "font-medium" : "opacity-60"}`}
              style={{
                borderColor: type === "expense" ? "var(--terracotta)" : "rgba(0,0,0,0.10)",
                backgroundColor: type === "expense" ? "rgba(212,88,58,0.06)" : "transparent",
                color: type === "expense" ? "var(--terracotta)" : "var(--ink)",
              }}
            >Expense</button>
            <button
              type="button"
              onClick={() => setType("income")}
              data-testid="add-tx-type-income"
              className={`px-3 py-2 rounded-md text-sm border transition-colors ${type === "income" ? "font-medium" : "opacity-60"}`}
              style={{
                borderColor: type === "income" ? "var(--forest)" : "rgba(0,0,0,0.10)",
                backgroundColor: type === "income" ? "rgba(31,93,52,0.06)" : "transparent",
                color: type === "income" ? "var(--forest)" : "var(--ink)",
              }}
            >Income</button>
          </div>
          <div>
            <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="add-tx-category" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="add-tx-date" />
            </div>
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Amount ($)</label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="add-tx-amount" placeholder="0.00" />
            </div>
          </div>
          <div>
            <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Note</label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                       data-testid="add-tx-desc" placeholder="Clay from Bailey's Pottery Supply" maxLength={300} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="add-tx-submit" className="btn-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScanReceiptDialog({ open, onOpenChange, categories, onSaved }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  // Confirmable form fields
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("other");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  // Line-item split state — one entry per row the AI found. `include` toggles
  // whether the row is inserted; `category` is per-row (defaults to what the
  // AI suggested). This is a client-side working copy so the user can tune
  // each row before they hit Save.
  const [items, setItems] = useState([]);
  const [splitMode, setSplitMode] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null); setPreview(null); setResult(null); setScanning(false);
      setDate(""); setAmount(""); setCategory("other"); setDescription("");
      setItems([]); setSplitMode(false);
    }
  }, [open]);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image too large (max 6MB)."); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const scan = async () => {
    if (!preview) return;
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.post("/bookkeeping/scan-receipt", {
        image_base64: preview,
        mime_type: file?.type || "image/jpeg",
      });
      setResult(data);
      setDate(data.date || new Date().toISOString().slice(0, 10));
      setAmount(data.amount_cents ? (data.amount_cents / 100).toFixed(2) : "");
      setCategory(data.suggested_category || "other");
      setDescription(data.vendor || "");
      // Hydrate the line-items table. Each row gets a stable client id so
      // React doesn't lose focus while the user edits inline.
      const rows = (data.line_items || []).map((li, idx) => ({
        _cid: `${Date.now()}-${idx}`,
        include: true,
        name: li.name || "",
        amount: li.amount_cents ? (li.amount_cents / 100).toFixed(2) : "",
        category: li.suggested_category || data.suggested_category || "other",
      }));
      setItems(rows);
      // If AI found ≥ 2 line items, auto-enable split mode as the default —
      // that's the pitch after all.
      setSplitMode(rows.length >= 2);
      if (!data.amount_cents) {
        toast.warning("Couldn't read the total — please type it in.");
      } else if (rows.length >= 2) {
        toast.success(`Read ${rows.length} line items · $${(data.amount_cents / 100).toFixed(2)} total · ${Math.round((data.confidence || 0) * 100)}% confidence`);
      } else {
        toast.success(`Read $${(data.amount_cents / 100).toFixed(2)} · ${Math.round((data.confidence || 0) * 100)}% confidence`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setScanning(false);
    }
  };

  // Sum the currently-included split rows so the user can see if they match
  // the total the AI extracted (tax + tip usually means they won't — that's
  // fine, it's shown as a note).
  const includedSum = items.filter((r) => r.include)
                          .reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
  const totalDollars = parseFloat(amount) || 0;
  const splitDelta = totalDollars - includedSum;

  const save = async () => {
    if (splitMode && items.length > 0) {
      // Split path — bulk-insert each included row as its own transaction.
      const rows = items
        .filter((r) => r.include && (parseFloat(r.amount) || 0) > 0)
        .map((r) => ({
          date,
          type: "expense",
          category: r.category,
          amount_cents: Math.round(parseFloat(r.amount) * 100),
          description: (r.name || description || "").slice(0, 300),
        }));
      if (rows.length === 0) {
        toast.error("Select at least one line item to save.");
        return;
      }
      setSaving(true);
      try {
        const { data } = await api.post("/bookkeeping/transactions/bulk", {
          transactions: rows,
        });
        toast.success(`Saved ${data.inserted} line items.`);
        onOpenChange(false);
        onSaved?.();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally { setSaving(false); }
      return;
    }
    // Lump path — single transaction for the whole receipt.
    const dollars = parseFloat(amount);
    if (!dollars || dollars <= 0) { toast.error("Enter a positive amount."); return; }
    setSaving(true);
    try {
      await api.post("/bookkeeping/transactions", {
        date, type: "expense", category,
        amount_cents: Math.round(dollars * 100),
        description,
      });
      toast.success("Receipt logged.");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="scan-receipt-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Scan a receipt</DialogTitle>
          <DialogDescription>
            AI will pull out the vendor, date, total, and a suggested category. Look it over — the AI isn't perfect.
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="grid gap-3">
            <label
              data-testid="scan-file-drop"
              className="cursor-pointer flex flex-col items-center justify-center rounded-lg p-8 border-2 border-dashed hover:bg-[rgba(0,0,0,0.02)] transition-colors"
              style={{ borderColor: "rgba(0,0,0,0.15)" }}
            >
              <Camera className="h-8 w-8 mb-2" style={{ color: "var(--terracotta)" }} />
              <div className="text-sm font-medium">Tap to upload a receipt photo</div>
              <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>JPG or PNG, up to 6MB</div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="scan-file-input"
              />
            </label>
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="rounded-md overflow-hidden max-h-56" style={{ border: "1px solid rgba(0,0,0,0.08)" }}>
              <img src={preview} alt="Receipt preview" className="w-full object-contain max-h-56" data-testid="scan-preview-img" />
            </div>
            {!result && (
              <Button onClick={scan} disabled={scanning} data-testid="scan-run-btn" className="btn-primary">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                {scanning ? "Reading receipt…" : "Read with AI"}
              </Button>
            )}
            {result && (
              <>
                <div className="text-xs italic" style={{ color: "var(--ink-soft)" }}>
                  AI read this receipt with {Math.round((result.confidence || 0) * 100)}% confidence — please double-check before saving.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Date</label>
                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="scan-date" />
                  </div>
                  <div>
                    <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Amount ($)</label>
                    <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="scan-amount" />
                  </div>
                </div>
                <div>
                  <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Category</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="scan-category" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.expense.map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Vendor / note</label>
                  <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="scan-desc" />
                </div>

                {items.length > 0 && (
                  <div
                    className="rounded-xl p-4 mt-1"
                    data-testid="scan-line-items"
                    style={{
                      backgroundColor: "rgba(44,196,217,0.08)",
                      border: "1.5px solid var(--avt-teal)",
                    }}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <div
                          className="overline"
                          style={{ color: "var(--avt-teal)" }}
                        >
                          {items.length} line item{items.length === 1 ? "" : "s"} found
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
                          {splitMode
                            ? "Splitting into separate transactions — tax season loves you."
                            : "Save as one lump transaction. Toggle on to split."}
                        </div>
                      </div>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={splitMode}
                          onChange={(e) => setSplitMode(e.target.checked)}
                          data-testid="scan-split-toggle"
                          className="h-4 w-4"
                          style={{ accentColor: "var(--avt-teal)" }}
                        />
                        <span className="text-xs uppercase tracking-widest">Split</span>
                      </label>
                    </div>

                    {splitMode && (
                      <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
                        {items.map((row, i) => (
                          <div
                            key={row._cid}
                            data-testid={`scan-line-item-${i}`}
                            className="grid grid-cols-[auto_1fr_90px_130px] gap-2 items-center"
                          >
                            <input
                              type="checkbox"
                              checked={row.include}
                              data-testid={`scan-line-item-${i}-include`}
                              onChange={(e) => setItems((arr) => arr.map((r, ii) => ii === i ? { ...r, include: e.target.checked } : r))}
                              className="h-4 w-4"
                              style={{ accentColor: "var(--avt-teal)" }}
                            />
                            <Input
                              value={row.name}
                              data-testid={`scan-line-item-${i}-name`}
                              onChange={(e) => setItems((arr) => arr.map((r, ii) => ii === i ? { ...r, name: e.target.value } : r))}
                              className="h-8 text-sm"
                              placeholder="Item name"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={row.amount}
                              data-testid={`scan-line-item-${i}-amount`}
                              onChange={(e) => setItems((arr) => arr.map((r, ii) => ii === i ? { ...r, amount: e.target.value } : r))}
                              className="h-8 text-sm text-right"
                            />
                            <Select
                              value={row.category}
                              onValueChange={(v) => setItems((arr) => arr.map((r, ii) => ii === i ? { ...r, category: v } : r))}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`scan-line-item-${i}-category`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.expense.map((c) => (
                                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                        <div
                          className="mt-2 flex items-center justify-between text-xs pt-2"
                          style={{ borderTop: "1px dashed rgba(0,0,0,0.1)", color: "var(--ink-soft)" }}
                          data-testid="scan-split-summary"
                        >
                          <span>
                            Selected sum: <strong style={{ color: "var(--ink)" }}>${includedSum.toFixed(2)}</strong> of ${totalDollars.toFixed(2)} receipt total
                          </span>
                          {Math.abs(splitDelta) >= 0.01 && (
                            <span style={{ color: "var(--avt-pink)" }}>
                              (${Math.abs(splitDelta).toFixed(2)} in tax/tip/other stays with the receipt)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {result && (
            <Button onClick={save} disabled={saving} data-testid="scan-save-btn" className="btn-primary">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvDialog({ open, onOpenChange, onSaved }) {
  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (!open) { setFile(null); setPreviewRows(null); }
  }, [open]);

  const upload = async (f) => {
    setFile(f);
    setLoading(true);
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/bookkeeping/import-csv/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!previewRows?.rows) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/bookkeeping/import-csv/commit", {
        rows: previewRows.rows.filter((r) => r.valid),
      });
      toast.success(`Imported ${data.inserted} transaction(s).`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="csv-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Import CSV</DialogTitle>
          <DialogDescription>
            Need headers: <code>date, description, amount, type, category</code>. Negative amounts are treated as expenses.
          </DialogDescription>
        </DialogHeader>

        {!file ? (
          <label
            data-testid="csv-drop"
            className="cursor-pointer flex flex-col items-center justify-center rounded-lg p-8 border-2 border-dashed hover:bg-[rgba(0,0,0,0.02)] transition-colors"
            style={{ borderColor: "rgba(0,0,0,0.15)" }}
          >
            <Upload className="h-8 w-8 mb-2" style={{ color: "var(--forest)" }} />
            <div className="text-sm font-medium">Choose CSV</div>
            <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>Up to 2MB</div>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
              data-testid="csv-input"
            />
          </label>
        ) : loading ? (
          <div className="py-6 text-center" style={{ color: "var(--ink-soft)" }}>
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Parsing…
          </div>
        ) : previewRows ? (
          <div>
            <div className="text-sm mb-2">
              <strong>{previewRows.valid}</strong> valid, <strong>{previewRows.skipped}</strong> skipped
            </div>
            <div className="max-h-64 overflow-y-auto text-xs border rounded" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
              <table className="w-full" data-testid="csv-preview-table">
                <thead style={{ backgroundColor: "#fbf8f2" }}>
                  <tr>
                    <th className="px-2 py-1 text-left">Date</th>
                    <th className="px-2 py-1 text-left">Type</th>
                    <th className="px-2 py-1 text-left">Category</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                    <th className="px-2 py-1 text-left">Note / status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.rows.slice(0, 100).map((r, i) => (
                    <tr key={i} className={r.valid ? "" : "opacity-50"}>
                      <td className="px-2 py-1">{r.date || "—"}</td>
                      <td className="px-2 py-1">{r.type || "—"}</td>
                      <td className="px-2 py-1">{r.category || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">
                        {r.amount_cents ? centsToDollars(r.amount_cents) : "—"}
                      </td>
                      <td className="px-2 py-1">{r.valid ? (r.description || "") : `⚠︎ ${r.reason}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {previewRows?.valid > 0 && (
            <Button onClick={commit} disabled={committing} data-testid="csv-commit-btn" className="btn-primary">
              {committing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Import {previewRows.valid}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalesTaxSection({ year, salesTax, onAdd, onChanged }) {
  const totals = salesTax?.totals || { collected_cents: 0, remitted_cents: 0, outstanding_cents: 0 };
  const items = salesTax?.items || [];
  const byJ = salesTax?.by_jurisdiction || [];

  const toggleRemitted = async (row) => {
    try {
      await api.patch(`/bookkeeping/sales-tax/${row.id}`, { remitted: !row.remitted });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (row) => {
    if (!window.confirm("Delete this sales-tax entry?")) return;
    try {
      await api.delete(`/bookkeeping/sales-tax/${row.id}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <section className="mt-12" data-testid="sales-tax-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
            <Landmark className="h-3 w-3" /> Money held for the state
          </div>
          <h2 className="font-serif text-3xl mt-1">Sales tax collected.</h2>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Sales tax isn't your income — you're holding it for the state or city. Log what you collected at each event, then check it off when you file and remit.
          </p>
        </div>
        <Button onClick={onAdd} data-testid="sales-tax-add-btn" className="btn-primary rounded-full">
          <Plus className="h-4 w-4 mr-1.5" /> Log sales tax
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div data-testid="sales-tax-collected"
              className="card-warm rounded-lg p-5"
              style={{ backgroundColor: "#fff", borderLeft: "3px solid var(--terracotta)" }}>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Collected {year}</div>
          <div className="font-serif text-3xl mt-2" style={{ color: "var(--terracotta)" }}>
            {centsToDollars(totals.collected_cents)}
          </div>
        </div>
        <div data-testid="sales-tax-remitted"
              className="card-warm rounded-lg p-5"
              style={{ backgroundColor: "#fff", borderLeft: "3px solid var(--forest)" }}>
          <div className="overline" style={{ color: "var(--forest)" }}>Remitted</div>
          <div className="font-serif text-3xl mt-2" style={{ color: "var(--forest)" }}>
            {centsToDollars(totals.remitted_cents)}
          </div>
        </div>
        <div data-testid="sales-tax-outstanding"
              className="card-warm rounded-lg p-5"
              style={{ backgroundColor: "#fff", borderLeft: "3px solid var(--gold)" }}>
          <div className="overline" style={{ color: "var(--gold)" }}>Still to remit</div>
          <div className="font-serif text-3xl mt-2" style={{ color: "var(--gold)" }}>
            {centsToDollars(totals.outstanding_cents)}
          </div>
        </div>
      </div>

      {byJ.length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="sales-tax-by-jurisdiction">
          {byJ.map((j) => (
            <div key={j.jurisdiction}
                  className="flex items-center justify-between rounded-md px-3 py-2 text-sm"
                  style={{ border: "1px solid rgba(0,0,0,0.08)", backgroundColor: "#fbf8f2" }}>
              <span className="font-medium">{j.jurisdiction}</span>
              <span className="font-mono">
                <span style={{ color: "var(--terracotta)" }}>{centsToDollars(j.outstanding)}</span>
                <span className="opacity-50"> / {centsToDollars(j.collected)}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {(salesTax?.by_event || []).length > 0 && (
        <div className="mt-4" data-testid="sales-tax-by-event">
          <div className="overline mb-2" style={{ color: "var(--ink-soft)" }}>By event</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {salesTax.by_event.map((e) => (
              <div key={e.event_id}
                    data-testid={`sales-tax-event-card-${e.event_id}`}
                    className="rounded-md px-3 py-2.5 text-sm"
                    style={{ border: "1px solid rgba(31,93,52,0.20)", backgroundColor: "rgba(31,93,52,0.04)" }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{e.event_title}</span>
                  <span className="font-mono text-xs whitespace-nowrap"
                        style={{ color: e.outstanding > 0 ? "var(--terracotta)" : "var(--forest)" }}>
                    {centsToDollars(e.outstanding)} outstanding
                  </span>
                </div>
                <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>
                  {e.entries} entr{e.entries === 1 ? "y" : "ies"} · collected {centsToDollars(e.collected)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div data-testid="sales-tax-empty"
              className="mt-6 card-warm rounded-lg p-10 text-center"
              style={{ backgroundColor: "#fff" }}>
          <div className="font-serif text-xl">Nothing logged yet for {year}.</div>
          <div className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
            Log what you collected at each event — it's a great habit that saves surprise at filing time.
          </div>
        </div>
      ) : (
        <div className="mt-6 card-warm rounded-lg overflow-hidden" style={{ backgroundColor: "#fff" }}>
          <table className="w-full text-sm" data-testid="sales-tax-table">
            <thead>
              <tr style={{ backgroundColor: "#fbf8f2", color: "var(--ink-soft)" }}>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Jurisdiction</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Note</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Tax</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Gross sales</th>
                <th className="text-center px-4 py-3 font-medium text-xs uppercase tracking-wider">Remitted</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} data-testid={`sales-tax-row-${r.id}`}
                    className="border-t" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
                  <td className="px-4 py-3 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3">{r.jurisdiction}</td>
                  <td className="px-4 py-3">
                    {r.note || <span style={{ color: "var(--ink-soft)" }}>—</span>}
                    {r.event_title && (
                      <span
                        data-testid={`sales-tax-event-${r.id}`}
                        className="ml-2 text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                        style={{
                          backgroundColor: "rgba(31,93,52,0.08)",
                          color: "var(--forest)",
                        }}
                      >
                        {r.event_title}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono" style={{ color: "var(--terracotta)" }}>
                    {centsToDollars(r.tax_collected_cents)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono" style={{ color: "var(--ink-soft)" }}>
                    {r.gross_sales_cents ? centsToDollars(r.gross_sales_cents) : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => toggleRemitted(r)}
                      data-testid={`sales-tax-toggle-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors"
                      style={{
                        borderColor: r.remitted ? "var(--forest)" : "rgba(0,0,0,0.15)",
                        color: r.remitted ? "var(--forest)" : "var(--ink-soft)",
                        backgroundColor: r.remitted ? "rgba(31,93,52,0.08)" : "transparent",
                      }}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      {r.remitted ? "Remitted" : "Not yet"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => del(r)}
                      data-testid={`sales-tax-delete-${r.id}`}
                      className="text-xs opacity-40 hover:opacity-100 transition-opacity"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MileageDialog({ open, onOpenChange, myEvents = [], onSaved }) {
  const today = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today());
  const [miles, setMiles] = useState("");
  const [roundTrip, setRoundTrip] = useState(true);
  const [description, setDescription] = useState("");
  const [eventId, setEventId] = useState("");
  const [rate, setRate] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setDate(today()); setMiles(""); setRoundTrip(true);
      setDescription(""); setEventId("");
    }
  }, [open]);

  // Fetch the IRS rate whenever the trip date changes so we can show a live preview
  useEffect(() => {
    if (!open || !date) return;
    api.get(`/bookkeeping/mileage-rate?date=${date}`)
      .then((r) => setRate(r.data.rate_cents_per_mile || 0))
      .catch(() => setRate(0));
  }, [open, date]);

  const numericMiles = parseFloat(miles) || 0;
  const totalMiles = numericMiles * (roundTrip ? 2 : 1);
  const previewCents = Math.round(totalMiles * rate);

  const submit = async () => {
    if (numericMiles <= 0) { toast.error("Enter miles greater than 0."); return; }
    setSaving(true);
    try {
      await api.post("/bookkeeping/mileage", {
        date, miles: numericMiles, round_trip: roundTrip, description,
        event_id: eventId || null,
      });
      toast.success(`${totalMiles} mi logged.`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mileage-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Log a trip</DialogTitle>
          <DialogDescription>
            Driving to an event, a supplier, or a customer meeting? Log the miles and we'll price them at the current IRS business rate.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mileage-date" />
            </div>
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Miles (one-way)</label>
              <Input type="number" step="0.1" min="0" value={miles}
                     onChange={(e) => setMiles(e.target.value)}
                     data-testid="mileage-miles" placeholder="12.4" />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--ink-soft)" }}>
            <input type="checkbox" checked={roundTrip}
                    onChange={(e) => setRoundTrip(e.target.checked)}
                    data-testid="mileage-round-trip" className="h-4 w-4" />
            Round trip (double the miles)
          </label>
          {myEvents.length > 0 && (
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Link to an event (optional)</label>
              <Select value={eventId || "none"} onValueChange={(v) => setEventId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="mileage-event-select" className="mt-1">
                  <SelectValue placeholder="No event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No event</SelectItem>
                  {myEvents.map((ev) => (
                    <SelectItem key={ev.id} value={ev.id}>
                      {ev.title} · {ev.date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs" style={{ color: "var(--ink-soft)" }}>What was this trip for?</label>
            <Textarea rows={2} value={description}
                       onChange={(e) => setDescription(e.target.value)}
                       data-testid="mileage-desc"
                       placeholder="Drove to Fall Makers Market, Brooklyn"
                       maxLength={200} />
          </div>
          {numericMiles > 0 && rate > 0 && (
            <div data-testid="mileage-preview"
                  className="rounded-md p-3 text-sm"
                  style={{ backgroundColor: "rgba(201,162,75,0.08)",
                            border: "1px solid rgba(201,162,75,0.30)" }}>
              <div style={{ color: "var(--ink-soft)" }} className="text-xs">Deduction</div>
              <div className="font-serif text-2xl mt-0.5" style={{ color: "var(--gold)" }}>
                {centsToDollars(previewCents)}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
                {totalMiles} mi × {rate}¢/mi (IRS business rate on {date})
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || numericMiles <= 0}
                   data-testid="mileage-submit" className="btn-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Log trip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalesTaxDialog({ open, onOpenChange, myEvents = [], onSaved }) {
  const today = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today());
  const [jurisdiction, setJurisdiction] = useState("");
  const [tax, setTax] = useState("");
  const [gross, setGross] = useState("");
  const [note, setNote] = useState("");
  const [eventId, setEventId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setDate(today()); setJurisdiction(""); setTax(""); setGross("");
      setNote(""); setEventId("");
    }
  }, [open]);

  const submit = async () => {
    const taxNum = parseFloat(tax);
    if (!taxNum || taxNum < 0) { toast.error("Enter the tax amount collected."); return; }
    if (!jurisdiction.trim()) { toast.error("Which state / city is this for?"); return; }
    setSaving(true);
    try {
      await api.post("/bookkeeping/sales-tax", {
        date,
        jurisdiction: jurisdiction.trim(),
        tax_collected_cents: Math.round(taxNum * 100),
        gross_sales_cents: gross ? Math.round(parseFloat(gross) * 100) : null,
        event_id: eventId || null,
        note,
      });
      toast.success("Logged.");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sales-tax-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Log sales tax</DialogTitle>
          <DialogDescription>
            Enter what you collected at one event or day. You can group by state, county, or city — whatever matches how you file.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sales-tax-date" />
            </div>
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Jurisdiction</label>
              <Input value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}
                     data-testid="sales-tax-jurisdiction"
                     placeholder="Brooklyn, NY" maxLength={80} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Tax collected ($)</label>
              <Input type="number" step="0.01" min="0" value={tax}
                     onChange={(e) => setTax(e.target.value)}
                     data-testid="sales-tax-amount" placeholder="36.00" />
            </div>
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Gross sales ($) — optional</label>
              <Input type="number" step="0.01" min="0" value={gross}
                     onChange={(e) => setGross(e.target.value)}
                     data-testid="sales-tax-gross" placeholder="450.00" />
            </div>
          </div>
          <div>
            <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Event or note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid="sales-tax-note"
                    placeholder="Fall Makers Market" maxLength={200} />
          </div>
          {myEvents.length > 0 && (
            <div>
              <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Link to an event (optional)</label>
              <Select value={eventId || "none"} onValueChange={(v) => setEventId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="sales-tax-event-select" className="mt-1">
                  <SelectValue placeholder="No event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No event</SelectItem>
                  {myEvents.map((ev) => (
                    <SelectItem key={ev.id} value={ev.id}>
                      {ev.title} · {ev.date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                   data-testid="sales-tax-submit" className="btn-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
